import React from "react";
import styles from "./style.module.scss";
import TvEnergyDataManager from "../../components/DataManagers/TVEnergyDataManager";
import EnergyTable from "../../components/TVEnergy/EnergyTable";
import TargetGraph from "../../components/TVEnergy/TargetGraph";
import EnergyGraphDelta from "../../components/TVEnergy/EnergyGraphDelta";
import TimingDataManager from "../../components/DataManagers/TimingDataManager";
import AuthenticatedLayout from "../../components/Layout/authenticatedLayout";
import { RootState } from "../../state";
import { isEqual } from "lodash";
import { useSelector } from "react-redux/es/hooks/useSelector";
import ImportsDataManager from "../../components/DataManagers/ImportsDataManager";

function TVEnergyPage() {
  const [selectedView, setSelectedView] = React.useState<
    | "graph"
    | "deltaRed"
    | "deltaBlue"
    | "deltaYellow"
    | "deltaGreen"
    | "deltaLeader"
  >("graph");

  const [autoAxis, setAutoAxis] = React.useState(true);
  const [yMin, setYMin] = React.useState<string>("");
  const [yMax, setYMax] = React.useState<string>("");

  const leader: string | undefined = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.standings?.overall?.active?.["1"]?.[
        "participant"
      ],
    isEqual,
  );

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  // Get driver names for each car color
  const getDriverName = (driverNumber: string) => {
    return driverInfo[driverNumber]?.driverName || driverNumber;
  };

  const redCarDriver = jagDrivers?.redCar || "9";
  const blueCarDriver = jagDrivers?.blueCar || "13";
  const yellowCarDriver = jagDrivers?.yellowCar || "14";
  const greenCarDriver = jagDrivers?.greenCar || "16";

  return (
    <AuthenticatedLayout title={"TV Energy"} showJaguarLogo={false}>
      <TimingDataManager />
      <TvEnergyDataManager />
      <ImportsDataManager />
      <div className={styles.container}>
        <div className={styles.energyTable}>
          <EnergyTable />
        </div>
        <div className={styles.energyGraphRegion}>
          <div className={styles.energyGraphContainer}>
            <div className={styles.radioMenu}>
              <label>
                <input
                  type="radio"
                  name="energyView"
                  value="graph"
                  checked={selectedView === "graph"}
                  onChange={() => setSelectedView("graph")}
                />
                <span style={{ color: "white" }}>TARGET GRAPH</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="energyView"
                  value="deltaRed"
                  checked={selectedView === "deltaRed"}
                  onChange={() => setSelectedView("deltaRed")}
                />
                <span
                  style={{
                    color: driverInfo[redCarDriver]?.driverHighlightColourAlt,
                  }}
                >
                  Δ {getDriverName(redCarDriver)}
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="energyView"
                  value="deltaBlue"
                  checked={selectedView === "deltaBlue"}
                  onChange={() => setSelectedView("deltaBlue")}
                />
                <span
                  style={{
                    color: driverInfo[blueCarDriver]?.driverHighlightColourAlt,
                  }}
                >
                  Δ {getDriverName(blueCarDriver)}
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="energyView"
                  value="deltaYellow"
                  checked={selectedView === "deltaYellow"}
                  onChange={() => setSelectedView("deltaYellow")}
                />
                <span
                  style={{
                    color:
                      driverInfo[yellowCarDriver]?.driverHighlightColourAlt,
                  }}
                >
                  Δ {getDriverName(yellowCarDriver)}
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="energyView"
                  value="deltaGreen"
                  checked={selectedView === "deltaGreen"}
                  onChange={() => setSelectedView("deltaGreen")}
                />
                <span
                  style={{
                    color: driverInfo[greenCarDriver]?.driverHighlightColourAlt,
                  }}
                >
                  Δ {getDriverName(greenCarDriver)}
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="energyView"
                  value="deltaLeader"
                  checked={selectedView === "deltaLeader"}
                  onChange={() => setSelectedView("deltaLeader")}
                />
                <span style={{ color: "white" }}>Δ Lead</span>
              </label>
              <div className={styles.axisControls}>
                {!autoAxis && (
                  <>
                    <div className={styles.axisInput}>
                      <label htmlFor="yMin">Min:</label>
                      <input
                        type="number"
                        id="yMin"
                        value={yMin}
                        onChange={(e) => {
                          setYMin(e.target.value);
                          console.log("Y-Axis Min:", e.target.value);
                        }}
                      />
                    </div>
                    <div className={styles.axisInput}>
                      <label htmlFor="yMax">Max:</label>
                      <input
                        type="number"
                        id="yMax"
                        value={yMax}
                        onChange={(e) => {
                          setYMax(e.target.value);
                          console.log("Y-Axis Max:", e.target.value);
                        }}
                      />
                    </div>
                  </>
                )}
                <div className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    id="autoAxis"
                    checked={autoAxis}
                    onChange={(e) => setAutoAxis(e.target.checked)}
                  />
                  <label htmlFor="autoAxis">Auto Y Axis</label>
                </div>
              </div>
            </div>
            <div className={styles.energyGraph}>
              {selectedView === "graph" && (
                <TargetGraph
                  yAxisMin={!autoAxis && yMin !== "" ? Number(yMin) : undefined}
                  yAxisMax={!autoAxis && yMax !== "" ? Number(yMax) : undefined}
                />
              )}
              {selectedView === "deltaRed" && (
                <EnergyGraphDelta
                  compareDriver={redCarDriver}
                  yAxisMin={!autoAxis && yMin !== "" ? Number(yMin) : undefined}
                  yAxisMax={!autoAxis && yMax !== "" ? Number(yMax) : undefined}
                />
              )}
              {selectedView === "deltaBlue" && (
                <EnergyGraphDelta
                  compareDriver={blueCarDriver}
                  yAxisMin={!autoAxis && yMin !== "" ? Number(yMin) : undefined}
                  yAxisMax={!autoAxis && yMax !== "" ? Number(yMax) : undefined}
                />
              )}
              {selectedView === "deltaYellow" && (
                <EnergyGraphDelta
                  compareDriver={yellowCarDriver}
                  yAxisMin={!autoAxis && yMin !== "" ? Number(yMin) : undefined}
                  yAxisMax={!autoAxis && yMax !== "" ? Number(yMax) : undefined}
                />
              )}
              {selectedView === "deltaGreen" && (
                <EnergyGraphDelta
                  compareDriver={greenCarDriver}
                  yAxisMin={!autoAxis && yMin !== "" ? Number(yMin) : undefined}
                  yAxisMax={!autoAxis && yMax !== "" ? Number(yMax) : undefined}
                />
              )}
              {selectedView === "deltaLeader" && leader && (
                <EnergyGraphDelta
                  compareDriver={leader}
                  yAxisMin={!autoAxis && yMin !== "" ? Number(yMin) : undefined}
                  yAxisMax={!autoAxis && yMax !== "" ? Number(yMax) : undefined}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}

export default TVEnergyPage;
